import { cloneDeep } from 'lodash';
import React from 'react';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { validateCostWidgetField } from '../validation';
import { IProposalCostCharges, IProposalPageFormState } from './form';
import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import PercentageFormField from '@components/form-fields/PercentageFormField';

interface IPolicyCostChargesWidgetProps {
  pageState: IProposalPageFormState;
  onPageStateUpdate: (pageState: IProposalPageFormState) => void;
  disabledForm?: boolean;
  status: string;
  editableLine: boolean;
  line: string;
  currencySymbol: string;
}

const PolicyCostChargesWidget: React.FC<IPolicyCostChargesWidgetProps> = ({
  pageState,
  onPageStateUpdate,
  disabledForm,
  status,
  editableLine,
  line,
  currencySymbol,
}) => {
  const values = pageState.values.costs;
  const errors = pageState.errors.costs;
  const touched = pageState.touched.costs;

  const disabledRow =
    disabledForm ||
    status?.toUpperCase() === 'CLOSED_WON' ||
    status?.toUpperCase() === 'CLOSED_LOST' ||
    !editableLine;

  const onFieldBlur = (fieldName: string) => {
    const newPageState = cloneDeep(pageState);
    newPageState.touched.costs[fieldName] = true;
    onPageStateUpdate(newPageState);
  };

  const onFieldUpdate = (
    fieldName: keyof IProposalCostCharges,
    value: IProposalCostCharges[keyof IProposalCostCharges],
    touched = false
  ) => {
    const newPageState = cloneDeep(pageState);

    (newPageState.values.costs as any)[fieldName] = value;
    newPageState.errors.costs[fieldName] = validateCostWidgetField(
      fieldName,
      value,
      newPageState.values.costs,
      line
    );

    if (fieldName === 'tpaFeesType') {
      newPageState.values.costs.tpaFeesPercentage = null;
      newPageState.values.costs.tpaFeesAmount = null;

      newPageState.errors.costs.tpaFeesPercentage = '';
      newPageState.errors.costs.tpaFeesAmount = '';

      newPageState.touched.costs.tpaFeesPercentage = false;
      newPageState.touched.costs.tpaFeesAmount = false;
    }

    if (touched) {
      newPageState.touched.costs[fieldName] = true;
    }

    onPageStateUpdate(newPageState);
  };

  return (
    <WidgetPaper>
      <WidgetSection title="Costs and Charges" isFieldsGrid fieldsPerRow={3}>
        <CurrencyFormField
          key="policyCost"
          name="policyCost"
          title="Policy Cost*"
          placeholder="Policy Cost"
          value={values.policyCost}
          error={touched.policyCost ? errors.policyCost : ''}
          onBlur={() => onFieldBlur('policyCost')}
          onChange={(v) => onFieldUpdate('policyCost', v.target.value)}
          disabled={disabledRow}
          material
          currencySymbol={currencySymbol}
        />

        <PercentageFormField
          key="chargesPercentage"
          name="chargesPercentage"
          title="Charges Percentage*"
          placeholder="Charges Percentage"
          value={values.chargesPercentage}
          error={touched.chargesPercentage ? errors.chargesPercentage : ''}
          onBlur={() => onFieldBlur('chargesPercentage')}
          onChange={(v) => onFieldUpdate('chargesPercentage', v.target.value)}
          disabled={disabledRow}
          material
        />
        {values.tpaFeesType === 'PERCENTAGE' ? (
          <PercentageFormField
            key="tpaFeesPercentage"
            name="tpaFeesPercentage"
            title="TPA Fees Percentage*"
            placeholder="TPA Fees Percentage"
            value={values.tpaFeesPercentage}
            error={touched.tpaFeesPercentage ? errors.tpaFeesPercentage : ''}
            onBlur={() => onFieldBlur('tpaFeesPercentage')}
            onChange={(v) => onFieldUpdate('tpaFeesPercentage', v.target.value)}
            disabled={disabledRow}
            material
          />
        ) : (
          <CurrencyFormField
            key="tpaFeesAmount"
            name="tpaFeesAmount"
            title="TPA Fees Amount*"
            placeholder="TPA Fees Amount"
            value={values.tpaFeesAmount}
            error={touched.tpaFeesAmount ? errors.tpaFeesAmount : ''}
            onBlur={() => onFieldBlur('tpaFeesAmount')}
            onChange={(v) => onFieldUpdate('tpaFeesAmount', v.target.value)}
            disabled={disabledRow}
            material
            currencySymbol={currencySymbol}
          />
        )}
      </WidgetSection>
    </WidgetPaper>
  );
};

export default PolicyCostChargesWidget;
